import { Route, Switch } from 'react-router';
import { CssBaseline } from '@material-ui/core';
import { BrowserRouter } from 'react-router-dom';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';

// components
import SignIn from './pages/SignIn/SignIn';
import Dashboard from './components/Dashboard/Dashboard';
import Overview from './pages/Overview/Overview';
import DisplayAlerts from './pages/Alerts/DisplayAlerts';
import CommandsSubMenu from './pages/Alerts/Commands/CommandsSubMenu';
import ProtectedRoute from './ProtectedRoute';
import DevicesMenu from './pages/Devices/DevicesMenu';
import TrendsSubMenu from './pages/Devices/Trends/TrendsSubMenu';
import Assets from './pages/Assets/DisplayAssets';
import ReportsMenu from './pages/Reports/ReportsMenu';
import HealthMenu from './pages/Health/Health';
import Config from './pages/Config/Config';
import Help from './pages/Help/Help';
import NBCItemSubMenu from './pages/NBC/NBC';
import NBCDrillDownSubMenu from './pages/NBC/Drilldown/Drilldown';
import TicketsMenu from './pages/Tickets/TicketsMenu';
import SettingsSubMenu from './pages/Config/Settings/SettingsSubMenu';
import FeaturesSubMenu from './pages/Config/Features/FeaturesSubMenu';
import PredictiveMaintenanceMenu from './pages/Maintenance/PredictiveMaintenanceMenu';
import NotificationsMenu from './pages/Notifications/NotificationsMenu';
import ConfigHistorySubMenu from './pages/Config/History/ConfigHistorySubMenu';
import AccessControlMenu from './pages/AccessControl/AccessControl';
import AccessControlHistorySubMenu from './pages/AccessControl/AccessControlHistory/AccessControlHistory';
import NBCHistorySubMenu from './pages/NBC/History/NBCHistorySubmenu';

// SSO imports
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import Auth0App from './Auth0App';
import { useEffect } from 'react';
import { setIdentityMgrType } from './store/UserSettings/UserSettingsActions';
import { IdentityManagerType } from './Interfaces/SSO/IdentityManagerConfiguration';
import { AccessControl } from './Interfaces/AccessControl';
import { ApplicationState } from './store';
import PreventiveMaintenanceMenu from './pages/Maintenance/PreventiveMaintenanceMenu';
import FirmwareSubMenu from './pages/Devices/Firmware/FirmwareSubMenu';
import { postRequestOptions } from './utils/utils';
import { isEqual } from 'lodash';
import { onLogout } from './store/UserSession/UserSessionActions';

import '../src/components/KendoGridColumnMenuCustomStyles.css';
import HistoricalLogs from './pages/LogsCollection/HistoricalLogs';
import ScheduledLogs from './pages/LogsCollection/ScheduledLogs';
import AlertsScheduledSubMenu from './pages/Alerts/AlertsScheduled/AlertsScheduledSubMenu';
import AccessControlScheduledSubMenu from './pages/AccessControl/AccessControlScheduled/AccessControlScheduledSubMenu';
import VisionSubMenu from './pages/Config/Vision/VisionSubMenu';
import ProductsHealthMenu from './pages/ProductsManagement/ProductsHealthMenu';
import ProductsImportMenu from './pages/ProductsManagement/ProductsImportMenu';
function App(props: {
  msal?: PublicClientApplication | undefined;
  auth0?: any;
  identityMgrType: IdentityManagerType;
}) {
  const token: string | undefined = useSelector(
    (state: ApplicationState) => state.userSessionReducer.sessionInfo?.Token,
  );
  const accessControl: AccessControl | undefined = useSelector(
    (state: ApplicationState) =>
      state.userSessionReducer.sessionInfo?.AccessControl,
  );
  const dispatch: Dispatch<any> = useDispatch();

  useEffect((): ReturnType<React.EffectCallback> => {
    setIdentityMgrType(dispatch, props.identityMgrType);
  }, []);

  useEffect((): ReturnType<React.EffectCallback> => {
    const fetchUserAccessControl = async () => {
      try {
        console.log('Fetching user access control');
        let response: Response = await fetch(
          'usersession/ValidateUserAccessControl',
          postRequestOptions(token, token),
        );
        if (response.ok) {
          const result: AccessControl =
            (await response.json()) as AccessControl;
          if (!isEqual(accessControl, result)) {
            console.log('AccessControl has been changed');
            onLogout(dispatch);
          }
        } else {
          console.log('Could not Validate AccessControl');
          onLogout(dispatch);
        }
      } catch (error) {
        console.log('Error fetching AccessControl');
        onLogout(dispatch);
      }
    };
    if (token) fetchUserAccessControl();
  }, []);

  switch (props.identityMgrType) {
    case IdentityManagerType.Msal:
      if (props.msal) {
        return (
          <MsalProvider instance={props.msal}>
            <Pages />
          </MsalProvider>
        );
      }
      break;
    case IdentityManagerType.Auth0:
      return <Auth0App auth0={props.auth0} />;
    default:
      break;
  }
  return <Pages />;
}

export function Pages() {
  function toggleTheme(): void {}

  const accessControl: AccessControl | undefined = useSelector(
    (state: ApplicationState) =>
      state.userSessionReducer.sessionInfo?.AccessControl,
  );

  return (
    <>
      <BrowserRouter>
        <CssBaseline />
        <Switch>
          <Route exact path="/" component={SignIn} />
          <Dashboard toggleTheme={toggleTheme} useDefaultTheme={false}>
            <ProtectedRoute
              path="/overview"
              component={Overview}
              showMenu={true}
            />
            <ProtectedRoute
              path="/alerts"
              component={DisplayAlerts}
              showMenu={accessControl?.Alerts?.ShowMenu ?? false}
            />
            <ProtectedRoute
              path="/commands"
              component={CommandsSubMenu}
              showMenu={accessControl?.Commands?.ShowMenu ?? false}
            />
            <ProtectedRoute
              path="/assets"
              component={Assets}
              showMenu={accessControl?.Assets?.ShowMenu ?? false}
            />
            <ProtectedRoute
              path="/productsImport"
              component={ProductsImportMenu}
              showMenu={accessControl?.ProductsImport?.ShowMenu ?? false}
            />
            <ProtectedRoute
              path="/productsHealth"
              component={ProductsHealthMenu}
              showMenu={accessControl?.ProductsHealth?.ShowMenu ?? false}
            />
            <ProtectedRoute
              path="/devices"
              component={DevicesMenu}
              showMenu={accessControl?.Devices?.ShowMenu ?? false}
            />
            <ProtectedRoute
              path="/firmware"
              component={FirmwareSubMenu}
              showMenu={accessControl?.Firmware?.ShowMenu ?? false}
            />
            <ProtectedRoute
              path="/prevmaint"
              component={PreventiveMaintenanceMenu}
              showMenu={accessControl?.PreventiveMaintenance?.ShowMenu ?? false}
            />
            <ProtectedRoute
              path="/predmaint"
              component={PredictiveMaintenanceMenu}
              showMenu={accessControl?.PredictiveMaintenance?.ShowMenu ?? false}
            />
            <ProtectedRoute
              path="/trends"
              component={TrendsSubMenu}
              showMenu={accessControl?.Trends?.ShowMenu ?? false}
            />
            <ProtectedRoute
              path="/reports"
              component={ReportsMenu}
              showMenu={accessControl?.Reports?.ShowMenu ?? false}
            />
            <ProtectedRoute
              path="/tickets"
              component={TicketsMenu}
              showMenu={accessControl?.Tickets?.ShowMenu ?? false}
            />
            <ProtectedRoute
              path="/health"
              component={HealthMenu}
              showMenu={accessControl?.Health?.ShowMenu ?? false}
            />
            <ProtectedRoute
              path="/itemsSubMenu"
              component={NBCItemSubMenu}
              showMenu={accessControl?.NBCItems?.ShowMenu ?? false}
            />
            <ProtectedRoute
              path="/drilldownSubMenu"
              component={NBCDrillDownSubMenu}
              showMenu={accessControl?.NBCDrilldown?.ShowMenu ?? false}
            />
            <ProtectedRoute
              path="/config"
              component={Config}
              showMenu={accessControl?.ConfigSettings?.ShowMenu ?? false}
            />
            <ProtectedRoute
              path="/notifications"
              component={NotificationsMenu}
              showMenu={accessControl?.Notifications?.ShowMenu ?? false}
            />
            <ProtectedRoute
              path="/help"
              component={Help}
              showMenu={accessControl?.Help?.ShowMenu ?? false}
            />
            <ProtectedRoute
              path="/settingsSubMenu"
              component={SettingsSubMenu}
              showMenu={accessControl?.ConfigSettings?.ShowMenu ?? false}
            />
            <ProtectedRoute
              path="/visionSubMenu"
              component={VisionSubMenu}
              showMenu={accessControl?.ConfigJsonSettings?.ShowMenu ?? false}
            />
            <ProtectedRoute
              path="/featuresSubMenu"
              component={FeaturesSubMenu}
              showMenu={accessControl?.ConfigFeatures?.ShowMenu ?? false}
            />
            <ProtectedRoute
              path="/configHistory"
              component={ConfigHistorySubMenu}
              showMenu={accessControl?.ConfigHistory?.ShowMenu ?? false}
            />
            <ProtectedRoute
              path="/historicalLogs"
              component={HistoricalLogs}
              showMenu={accessControl?.HistoricalLogs?.ShowMenu ?? false}
            />
            <ProtectedRoute
              path="/scheduledLogs"
              component={ScheduledLogs}
              showMenu={accessControl?.ScheduledLogs?.ShowMenu ?? false}
            />
            <ProtectedRoute
              path="/accessControl"
              component={AccessControlMenu}
              showMenu={accessControl?.AccessControlMenu?.ShowMenu ?? false}
            />
            <ProtectedRoute
              path="/accessControlHistory"
              component={AccessControlHistorySubMenu}
              showMenu={accessControl?.AccessControlMenu?.ShowMenu ?? false}
            />
            <ProtectedRoute
              path="/nbcHistory"
              component={NBCHistorySubMenu}
              showMenu={accessControl?.NBCHistory?.ShowMenu ?? false}
            />
            <ProtectedRoute
              path="/scheduledWeightImports"
              component={AlertsScheduledSubMenu}
              showMenu={accessControl?.AlertsScheduled?.ShowMenu ?? false}
            />
            <ProtectedRoute
              path="/scheduledConfigAccessControl"
              component={AccessControlScheduledSubMenu}
              showMenu={
                accessControl?.AccessControlScheduled?.ShowMenu ?? false
              }
            />
          </Dashboard>
        </Switch>
      </BrowserRouter>
    </>
  );
}

export default App;
